import React, { FC } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrgDropdown } from 'models/user';
import { useTranslation } from 'react-i18next';
import { getOrganizationsTemplate } from 'apollo/templates/organization';

type OrgsAutocompleteProps = {
    onOrgChange: (clinic: OrgDropdown | null) => void;
    value?: OrgDropdown | null;
    name?: string;
    type: string | null;
    local: boolean | null;
    isWhiteLabeled: boolean | null;
    helperText?: string;
    error?: boolean;
    fullWidth?: boolean;
    useInForm?: boolean;
    required?: boolean;
};

const OrgsAutocomplete: FC<OrgsAutocompleteProps> = ({
    onOrgChange,
    value = null,
    name,
    type,
    local,
    isWhiteLabeled,
    helperText,
    error,
    fullWidth,
    useInForm,
    required,
}) => {
    const { t } = useTranslation('users');
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading } = useQuery(getOrganizationsTemplate, {
        variables: {
            ...(type ? { type } : {}),
            ...(local !== null ? { local } : {}),
            ...(isWhiteLabeled !== null ? { isWhiteLabeled } : {}),
        },
        notifyOnNetworkStatusChange: true,
        onError: showBoundary,
    });

    return (
        <Autocomplete
            fullWidth={fullWidth}
            id="select-parent-clinic"
            value={value}
            options={data?.organizations ?? []}
            loading={loading}
            disableClearable={required}
            getOptionLabel={(option) => option?.name ?? ''}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            onChange={(_, selected) => {
                onOrgChange(selected);
            }}
            renderOption={(props, option) => (
                <li {...props} key={option._id}>
                    {option.name}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={name}
                    label={t('users:SELECT_ORGANIZATION')}
                    margin="dense"
                    required={required}
                    error={error}
                    helperText={helperText}
                    sx={{ minWidth: useInForm ? 0 : 300 }}
                />
            )}
        />
    );
};

export default OrgsAutocomplete;
